import { useEffect, useState } from "react";
import { SITES } from "@karrtopelka/game-api/registry";

interface SiteStatus {
  id: string;
  state: "up" | "down" | "retired";
  ms: number | null;
  code: number | null;
}

interface StatusPayload {
  checkedAt: string;
  sites: SiteStatus[];
}

/** Probing 12 origins takes a beat — dots stay "checking" until the answer lands. */
type DotState = "checking" | "up" | "down" | "retired";

function Dot({ state }: { state: DotState }) {
  const tone =
    state === "up"
      ? "bg-win shadow-[0_0_10px_rgba(74,222,128,0.65)]"
      : state === "down"
        ? "bg-danger shadow-[0_0_10px_rgba(248,113,113,0.6)]"
        : // retired and checking share the dead-grey dot; only retired stops animating
          "bg-text-faint";
  return (
    <span className="relative inline-flex h-2 w-2 shrink-0" aria-hidden="true">
      {state === "up" && (
        <span className="absolute inset-0 animate-ping rounded-full bg-win opacity-50" />
      )}
      <span
        className={`relative inline-block h-2 w-2 rounded-full ${tone} ${
          state === "checking" ? "animate-breathe" : ""
        }`}
      />
    </span>
  );
}

const shipped = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

export function Subdomains() {
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetch("/api/status")
      .then((r) => r.json() as Promise<StatusPayload>)
      .then((d) => live && setStatus(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const byId = new Map(status?.sites.map((s) => [s.id, s]) ?? []);
  // Retired sites are excluded from both halves of the ratio — switching a game off must
  // not make the header read as an outage.
  const expected = status?.sites.filter((s) => s.state !== "retired") ?? [];
  const upCount = expected.filter((s) => s.state === "up").length;
  const retiredCount = (status?.sites.length ?? 0) - expected.length;

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center bg-bg text-text"
      style={{
        paddingTop: "calc(3rem + env(safe-area-inset-top))",
        paddingBottom: "calc(3rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex w-full max-w-4xl animate-fade-up flex-col px-6">
        <a
          href="/"
          className="mb-6 w-fit font-mono text-xs text-text-faint transition-colors hover:text-accent"
        >
          ← karrtopelka<span className="text-accent">.</span>
        </a>
        <h1 className="font-display text-3xl font-extrabold tracking-tight lg:text-5xl">
          subdomains
          <span
            className="text-accent"
            style={{ textShadow: "0 0 24px rgba(255,111,165,0.65)" }}
          >
            .
          </span>
        </h1>
        <p className="mt-3 text-text-muted">
          everything deployed under <span className="font-mono">karrtopelka.com</span>. one
          worker each, probed live.
        </p>
        <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-accent/25 bg-surface px-3.5 py-1 font-mono text-xs text-text-muted">
          {status ? (
            <>
              <span className={upCount === expected.length ? "text-win" : "text-danger"}>
                {upCount}/{expected.length}
              </span>
              up{retiredCount > 0 ? ` · ${retiredCount} retired` : ""} · checked{" "}
              {new Date(status.checkedAt).toLocaleTimeString("en-GB", { timeZone: "UTC" })} utc
            </>
          ) : failed ? (
            <span className="text-danger">status probe unreachable</span>
          ) : (
            <>
              <Dot state="checking" />
              probing {SITES.length} origins
            </>
          )}
        </p>
      </header>

      <div className="mt-8 w-full max-w-4xl px-6">
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
          {SITES.map((site, i) => {
            const s = byId.get(site.id);
            const state: DotState = failed ? "down" : (s?.state ?? "checking");
            return (
              <li
                key={site.id}
                className="animate-fade-up"
                style={{ animationDelay: `${80 + i * 40}ms` }}
              >
                <a
                  href={site.origin}
                  className={`group flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-surface-raised sm:px-5 ${
                    state === "retired" ? "opacity-45" : ""
                  }`}
                >
                  <Dot state={state} />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span
                        className={`truncate font-mono text-base font-semibold ${
                          state === "retired"
                            ? "text-text-muted line-through decoration-1"
                            : "group-hover:text-accent"
                        }`}
                      >
                        {site.label}
                        <span className="text-text-faint">.karrtopelka.com</span>
                      </span>
                      {site.kind !== "game" && (
                        <span className="shrink-0 rounded-sm border border-border px-1.5 font-mono text-[10px] text-text-faint">
                          {site.kind}
                        </span>
                      )}
                      {state === "retired" && (
                        <span className="shrink-0 rounded-sm border border-border px-1.5 font-mono text-[10px] uppercase tracking-wide text-text-faint">
                          retired
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-text-muted">{site.blurb}</p>
                  </div>

                  <div className="hidden shrink-0 text-right font-mono text-xs text-text-faint sm:block">
                    <div>{shipped(site.since)}</div>
                    <div className={state === "down" ? "text-danger" : ""}>
                      {state === "checking"
                        ? "…"
                        : state === "retired"
                          ? "off"
                          : state === "down"
                            ? (s?.code ?? "no answer")
                            : // The hub can't probe itself (Cloudflare refuses a Worker
                              // fetching its own hostname) — it reports up with no timing.
                              s?.ms === null
                              ? "self"
                              : `${s?.ms}ms`}
                    </div>
                  </div>

                  <span className="shrink-0 text-sm text-text-faint transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-accent">
                    →
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      <footer
        className="mt-10 animate-fade-up px-6 text-center text-xs text-text-faint"
        style={{ animationDelay: "620ms" }}
      >
        one worker per subdomain · same-origin api · no cors
      </footer>
    </main>
  );
}
